import React, { ReactNode } from 'react'
import { cx } from '../cx'
import s from './styles.module.scss'

export interface TabItem<T extends string = string> {
    value: T
    label: ReactNode
    icon?: ReactNode
    count?: number | string
    /** Small attention dot (unsaved changes, pending items). */
    dot?: boolean
    disabled?: boolean
}

export interface TabsProps<T extends string = string> {
    items: TabItem<T>[]
    value: T
    onChange: (value: T) => void
    /** Vertical list for detail-page sub navigation. */
    orientation?: 'horizontal' | 'vertical'
    /** Drop the bottom margin (when the panel supplies its own spacing). */
    noMargin?: boolean
    /** Grey strip styling for a tab bar mounted inside a Card. */
    inCard?: boolean
    className?: string
    'aria-label'?: string
}

/** Underline tab bar (or vertical rail). Controlled: pass `value` and `onChange`. */
export function Tabs<T extends string = string>({ items, value, onChange, orientation = 'horizontal', noMargin = false, inCard = false, className, ...rest }: TabsProps<T>) {
    const onKey = (e: React.KeyboardEvent, index: number) => {
        const enabled = items.filter((i) => !i.disabled)
        const pos = enabled.findIndex((i) => i.value === items[index].value)
        const forward = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight'
        const back = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft'
        if (e.key === forward) onChange(enabled[(pos + 1) % enabled.length].value)
        if (e.key === back) onChange(enabled[(pos - 1 + enabled.length) % enabled.length].value)
    }

    return (
        <div className={cx(orientation === 'vertical' ? s.vertical : s.bar, noMargin && s.noMargin, inCard && s.inCard, className)} role="tablist" aria-orientation={orientation} aria-label={rest['aria-label']}>
            {items.map((item, index) => {
                const active = item.value === value
                return (
                    <button
                        key={item.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        tabIndex={active ? 0 : -1}
                        disabled={item.disabled}
                        className={cx(s.tab, active && s.active)}
                        onClick={() => onChange(item.value)}
                        onKeyDown={(e) => onKey(e, index)}
                    >
                        {item.icon}
                        {item.label}
                        {item.count != null && <span className={s.count}>{item.count}</span>}
                        {item.dot && <span className={s.dot} aria-hidden="true" />}
                    </button>
                )
            })}
        </div>
    )
}

export const TabPanel = ({ children, className }: { children: ReactNode; className?: string }) => (
    <div role="tabpanel" className={cx(s.panel, className)}>
        {children}
    </div>
)
